import { createContext, useEffect, useReducer } from "react";
import axios from "axios";
import { MatxLoading } from "app/components";
import { api } from "../../api/API";
import { Settings } from "@mui/icons-material";
import { getNavigations } from "../navigations";
import appStore from "../mobxStore/AppStore";
const initialState = {
  user: null,
  isInitialised: false,
  isAuthenticated: false,
};

// const isValidToken = (accessToken) => {
//   if (!accessToken) return false;

//   const decodedToken = jwtDecode(accessToken);
//   const currentTime = Date.now() / 1000;
//   return decodedToken.exp > currentTime;
// };

// const setSession = (accessToken) => {
//   if (accessToken) {
//     localStorage.setItem('accessToken', accessToken);
//     axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
//   } else {
//     localStorage.removeItem('accessToken');
//     delete axios.defaults.headers.common.Authorization;
//   }
// };

const getStoredEntityId = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("selectedEntity");
    if (!raw) return null;
    const parsed = Number(raw);
    return !Number.isNaN(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const resolveEntityId = (authData) => {
  if (
    typeof appStore.selectedEntity === "number" &&
    appStore.selectedEntity > 0
  ) {
    return appStore.selectedEntity;
  }

  const storedEntityId = getStoredEntityId();
  if (storedEntityId) {
    return storedEntityId;
  }

  const entitiesString = authData?.user?.[0]?.entities;
  if (typeof entitiesString === "string" && entitiesString.trim().length > 0) {
    const candidate = entitiesString
      .split(",")
      .map((value) => Number(value.trim()))
      .find((value) => !Number.isNaN(value) && value > 0);
    if (candidate) {
      return candidate;
    }
  }

  return null;
};

const refreshDashboardData = async (authData) => {
  try {
    const userId = authData?.user?.[0]?.userId;
    const entityId = resolveEntityId(authData);
    if (!userId || !entityId) {
      return;
    }
    const response = await api.post(`user-dashboard`, {
      body: {
        userId,
        entityId,
      },
    });
    if (response?.success && response?.data) {
      const updatedAuthData = {
        ...authData,
        pendingTasks: response.data.pendingApplicationTasks,
        pendingAssignedTasks: response.data.pendingAssignedTasks || {},
        tasks: response.data.tasks || authData.tasks,
        dashboardCounts:
          response.data.dashboardCounts || authData.dashboardCounts,
      };
      appStore.setLoginResponse(updatedAuthData);
      sessionStorage.setItem("authData", JSON.stringify(updatedAuthData));
    }
  } catch (error) {
    console.error(
      "Failed to refresh dashboard data after token validation",
      error
    );
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return { ...state, isAuthenticated, isInitialised: true, user };
    }

    case "LOGIN": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: true, user };
    }

    case "LOGOUT": {
      return { ...state, isAuthenticated: false, user: null };
    }

    case "REGISTER": {
      const { user } = action.payload;

      return { ...state, isAuthenticated: true, user };
    }

    default:
      return state;
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => {},
  logout: () => {},
  register: () => {},
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const login = async (username, password) => {
    const response = await api.post(`login`, {
      body: {
        userName: username,
        userPassword: password,
      },
    });
    if (response.success) {
      const { user } = response;
      const authDataJSON = JSON.stringify(response);
      api.setAuthToken(response.accessToken);
      appStore.setLoginResponse(response);
      api.setNavigations(response.navigations);
      sessionStorage.setItem("authData", authDataJSON);
      dispatch({ type: "LOGIN", payload: { user } });
    } else {
      //show toast message
    }
  };

  const register = async (email, username, password, firstname, lastname) => {
    const response = await api.post(`create-user`, {
      body: {
        userName: username,
        userEmail: email,
        userPassword: password,
        userFirstName: firstname,
        userLastName: lastname,
        userImage: "https://example.com/profile-image.jpg",
        userAddress: "123 Main St, City, Country",
        userServerEmail: email,
        userPhoneOne: "123-456-7890",
        userPhoneTwo: "987-654-3210",
        userEnabled: true,
        userLocked: false,
      },
    });
    const { user } = response;
    api.setAuthToken(response.accessToken);
    api.setNavigations(response.navigations);
    appStore.setLoginResponse(response);
    const authDataJSON = JSON.stringify(response);
    sessionStorage.setItem("authData", authDataJSON);
    dispatch({ type: "REGISTER", payload: { user } });
  };

  const logout = async () => {
    const token = appStore.loginResponse?.accessToken;
    const userId = appStore.loginResponse?.user?.[0]?.userId;
    try {
      await api.post(`logout`, {
        body: {
          accessToken: token,
          userId,
        },
      });
    } catch {}
    sessionStorage.removeItem("authData");
    api.setAuthToken(null);
    appStore.clearSession();
    dispatch({ type: "LOGOUT" });
  };

  useEffect(() => {
    const jsonUser = sessionStorage.getItem("authData");
    const authData = JSON.parse(jsonUser);
    if (authData) {
      (async () => {
        try {
          const response = await api.post("check-token", {
            body: {
              accessToken: authData.accessToken,
              userId: authData.user[0].userId,
            },
          });
          if (response.success) {
            api.setAuthToken(authData.accessToken);
            appStore.setLoginResponse(authData);
            api.setNavigations(authData.navigations);
            dispatch({
              type: "INIT",
              payload: { isAuthenticated: true, user: authData.user[0] },
            });
            refreshDashboardData(authData);
          } else {
            dispatch({
              type: "INIT",
              payload: { isAuthenticated: false, user: null },
            });
          }
        } catch (err) {
          console.error(err);
          dispatch({
            type: "INIT",
            payload: { isAuthenticated: false, user: null },
          });
        }
      })();
    } else {
      dispatch({
        type: "INIT",
        payload: { isAuthenticated: false, user: null },
      });
    }
  }, []);

  // SHOW LOADER
  if (!state.isInitialised) return <MatxLoading />;

  return (
    <AuthContext.Provider
      value={{ ...state, method: "JWT", login, logout, register }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
